/* eslint-disable react-refresh/only-export-components */
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { services } from "@/data";
import { useState } from "react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_public/services/$slug")({
  loader: ({ params }) => {
    const service = services.find((s) => s.slug === params.slug);
    if (!service) throw notFound();
    return { service };
  },
  component: ServiceDetailPage,
});

function ServiceDetailPage() {
  const { service } = Route.useLoaderData();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const related = services.filter((s) =>
    service.relatedServices.includes(s.slug)
  );

  return (
    <>
      <Helmet>
        <title>{service.metaTitle}</title>
        <meta name="description" content={service.metaDescription} />
        <link
          rel="canonical"
          href={`https://rosewoodtax.co.ke/services/${service.slug}`}
        />
      </Helmet>

      <section className="relative bg-charcoal pt-16 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/90 to-charcoal/10" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-white/70 mb-8">
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <span>/</span>
            <Link to="/services" className="hover:text-white transition-colors">
              Services
            </Link>
            <span>/</span>
            <span className="text-white font-medium">{service.title}</span>
          </nav>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="text-4xl md:text-5xl font-semibold text-white mb-5 leading-tight">
                {service.title}
              </h1>
              <p className="text-xl text-white/80 leading-relaxed mb-8">
                {service.shortDescription}
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-charcoal font-semibold rounded-xl hover:bg-gold-light transition-colors"
              >
                Get Started <span className="text-lg">→</span>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-7 text-white"
            >
              <h3 className="font-semibold mb-5 text-gold-light">
                What’s Included
              </h3>
              <ul className="space-y-3">
                {service.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-gold-light rounded-full mt-2 flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-14">
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl font-semibold text-charcoal mb-6">
                  About This Service
                </h2>
                <div className="prose prose-lg text-neutral-800 leading-relaxed space-y-4">
                  {service.fullDescription.split("\n\n").map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="mt-14"
              >
                <h2 className="text-3xl font-semibold text-charcoal mb-8">
                  Our Process
                </h2>
                <div className="grid sm:grid-cols-2 gap-5">
                  {service.process.map((step, i) => (
                    <motion.div
                      key={step.step}
                      initial={{ opacity: 0, y: 15 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.08 }}
                      className="flex gap-4 p-5 bg-cream rounded-xl"
                    >
                      <div className="w-9 h-9 bg-burgundy rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-gold-light font-bold text-sm">
                          {step.step}
                        </span>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">{step.title}</h4>
                        <p className="text-sm text-neutral-800 leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mt-14"
              >
                <h2 className="text-3xl font-semibold text-charcoal mb-8">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-3">
                  {service.faqs.map((faq, i) => (
                    <div key={i} className="rounded-xl overflow-hidden">
                      <button
                        onClick={() => setOpenFaq(openFaq === i ? null : i)}
                        className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-cream transition-colors"
                      >
                        <span className="font-medium text-charcoal">
                          {faq.question}
                        </span>
                        <span
                          className={cn(
                            "text-charcoal text-lg leading-none transition-transform duration-200",
                            openFaq === i && "rotate-45"
                          )}
                        >
                          +
                        </span>
                      </button>
                      <AnimatePresence initial={false}>
                        {openFaq === i && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="px-5 pb-5"
                          >
                            <p className="text-neutral-800 leading-relaxed text-sm">
                              {faq.answer}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            <div className="space-y-6">
              <div className="bg-burgundy rounded-2xl p-7 text-white">
                <h3 className="text-2xl font-semibold mb-2">
                  Ready to Get Started?
                </h3>
                <p className="text-white/70 text-sm mb-6 leading-relaxed">
                  Book a free consultation with our {service.title} specialists
                  today.
                </p>
                <Link
                  to="/contact"
                  className="block text-center px-5 py-3 bg-gold text-charcoal font-semibold rounded-xl hover:bg-gold-light transition-colors text-sm mb-3"
                >
                  Book Free Consultation
                </Link>
                <a
                  href="tel:+254703173405"
                  className="block text-center px-5 py-3 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors text-sm"
                >
                  +254 703 173405
                </a>

                <div className="mt-6 pt-5 space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-gold rounded-full" />
                    <span className="text-white/70 text-xs">
                      Free initial consultation
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-gold rounded-full" />
                    <span className="text-white/70 text-xs">
                      ICPAK certified experts
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-gold rounded-full" />
                    <span className="text-white/70 text-xs">
                      100% confidential
                    </span>
                  </div>
                </div>
              </div>

              {related.length > 0 && (
                <div className="bg-cream rounded-2xl p-6">
                  <h4 className="font-semibold text-charcoal mb-4 text-sm uppercase tracking-wider">
                    Related Services
                  </h4>
                  <div className="space-y-3">
                    {related.map((s) => (
                      <Link
                        key={s.id}
                        to={`/services/${s.slug}` as never}
                        className="flex items-center gap-4 p-3 rounded-lg hover:bg-white transition-colors group"
                      >
                        <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={s.image}
                            alt={s.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <span className="text-sm font-medium text-charcoal group-hover:text-burgundy transition-colors">
                          {s.title}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}